import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { clearCityDetails } from "./citySlice";
import { getAllActiveCountries } from "../country/services/country.services";
import { getAllActiveStates } from "../state/services/state.services";
import {
  createCity,
  getAllCities,
  getCityByID,
  updateCity,
} from "./services/city.services";
import { IState } from "../state/stateModel";
import { findIndex } from "lodash";
import { closed } from "../../components/drawer/drawerSlice";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
  StateList: IState[];
}

const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  StateList,
  formRenderProps,
}) => {
  const stateID = formRenderProps.valueGetter("StateID");

  useEffect(() => {
    const StateID = stateID ? stateID : undefined;
    const stateIndex = findIndex(StateList, { ID: StateID });
    if (stateIndex > -1) {
      const state = StateList[stateIndex];
      formRenderProps.onChange("CountryID", { value: state.CountryID });
    } else {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [stateID]);
  return null;
};

const CreateCity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CityDetail = useAppSelector((state) => state.city.CityDetail);
  const CityID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.city.formLoading);

  useEffect(() => {
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveStates());
    return () => {
      dispatch(clearCityDetails());
      dispatch(closed());
    };
  }, []);
  useEffect(() => {
    if (CityID) {
      dispatch(getCityByID(CityID));
    }
  }, [CityID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!CityID) {
        const payload = {
          CityName: values.CityName,
          StateID: values.StateID,
          CountryID: values.CountryID,
          IsActive: true,
        };
        const response = await dispatch(createCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCityDetails());
          dispatch(getAllCities());
        }
      } else {
        const payload = {
          ID: CityID,
          CityName: values.CityName,
          StateID: values.StateID,
          CountryID: values.CountryID,
          IsActive: values.IsActive,
        };
        const response = await dispatch(updateCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCityDetails());
          dispatch(getAllCities());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="CityName"
                label="City"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"StateID"}
                name={"StateID"}
                label={"State"}
                component={FormSelectionField}
                validator={requiredValidator}
                options={StateList.map((state: any) => {
                  return {
                    value: state.ID,
                    label: state.StateName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                // id={"CountryID"}
                name={"CountryID"}
                label={"Country"}
                component={FormSelectionField}
                // validator={requiredValidator}
                disabled={true}
                options={CountryList.map((country: any) => {
                  return {
                    value: country.ID,
                    label: country.CountryName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label="Save"
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
            <StateChangeWatcher
              StateList={StateList}
              formRenderProps={formRenderProps}
            />
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCity;
